/**
 * Orders Page
 *
 * Displays the user's order history fetched from Supabase.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return 'success';
    case 'processing': return 'info';
    case 'pending': return 'warning';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const Orders = () => {
  const { customer } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!customer?.id) return;

    const fetchOrders = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select('id, status, total, created_at, order_items(count)')
          .eq('customer_id', customer.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setOrders(data ?? []);
      } catch (err) {
        setError(err.message || 'Failed to fetch orders');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [customer?.id]);

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/account" style={{ textDecoration: 'none', color: '#8B4513' }}>
          My Account
        </Link>
        <Typography color="text.primary">Orders</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={Link}
          to="/account"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2, color: '#8B4513' }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: '#8B4513' }}>
          Order History
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your Orders
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#8B4513' }} />
          </Box>
        ) : orders.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell component="th" scope="row">
                      {order.id.slice(0, 8)}
                    </TableCell>
                    <TableCell>{formatDate(order.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={(order.status || 'pending').charAt(0).toUpperCase() + (order.status || 'pending').slice(1)}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {order.order_items?.[0]?.count ?? 0}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(order.total ?? 0)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={Link}
                        to={`/account/orders/${order.id}`}
                        startIcon={<VisibilityIcon />}
                        size="small"
                        sx={{
                          color: '#8B4513',
                          '&:hover': { backgroundColor: 'rgba(139, 69, 19, 0.08)' }
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" paragraph>
              You haven&apos;t placed any orders yet.
            </Typography>
            <Button
              component={Link}
              to="/products"
              variant="contained"
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
            >
              Start Shopping
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Orders;
